import useCustomer from 'src/app/customers/hooks/useCustomer';
import { getCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSelector';
import { useAppSelector } from 'src/store';
import authService from 'src/services/auth-service';
import useAuthService from './useAuthService';
import { Resources } from '../types/graph/resources';
import { Access, ResourceAccess, Role, UserGroup, UserPermissions } from '../types/graph/user';

const defaultUserPermissions: UserPermissions = {
    customerLevelPermissions: [],
    projectLevelPermissions: [],
    tnc_consent: false,
    is_internal_user: false,
    token: '',
};

const defaultResourceAccess: ResourceAccess = {
    [Access.VIEW]: [],
    [Access.CREATE]: [],
    [Access.UPDATE]: [],
    [Access.DELETE]: [],
};

type HasCustomerLevelPermissionParams = {
    customerId?: string;
    to: Access | Access[];
    resource: Resources;
};

type HasProjectLevelPermissionParams = {
    projectId?: string;
    to: Access | Access[];
    resource: Resources;
};

type HasPermissionParams = {
    projectId?: string | null;
    customerId?: string | null;
    to: Access | Access[];
    resource: Resources;
};

type HasCustomerLevelRoleParams = {
    customerId: string;
    role: Role | Role[];
};

type HasProjectLevelRoleParams = {
    projectId: string;
    role: Role | Role[];
};

const usePermissions = () => {
    const { isCurrentUserSuperAdmin, currentUser } = useAuthService();

    const { currentCustomerId } = useCustomer();
    const currentProjectId = useAppSelector(getCurrentProjectId);

    const getUserPermissions = () => {
        return authService.getUserPermissions() || defaultUserPermissions;
    };

    const getPermissionsForAllCustomers = () => {
        return getUserPermissions().customerLevelPermissions;
    };

    const getPermissionsForOneCustomer = (customerId?: string) => {
        const allCustomerPermissions = getPermissionsForAllCustomers();
        return allCustomerPermissions?.find(entry => entry.customerId === customerId);
    };

    const getRolesForOneCustomer = (customerId: string) => {
        const selectedCustomerPermissions = getPermissionsForOneCustomer(customerId);
        return selectedCustomerPermissions?.roles || [];
    };

    const getPermissionsForAllProjects = () => {
        return getUserPermissions().projectLevelPermissions;
    };

    const getPermissionsForOneProject = (projectId?: string) => {
        const allProjectPermissions = getPermissionsForAllProjects();
        return allProjectPermissions?.find(entry => entry.projectId === projectId);
    };

    const getRolesForOneProject = (projectId: string) => {
        const selectedProjectPermissions = getPermissionsForOneProject(projectId);
        return selectedProjectPermissions?.roles || [];
    };

    const getResourceAccessForOneCustomer = (customerId?: string) => {
        const selectedCustomerPermissions = getPermissionsForOneCustomer(customerId);
        return selectedCustomerPermissions?.permissions || defaultResourceAccess;
    };

    const getResourceAccessForOneProject = (projectId?: string) => {
        const selectedProjectPermissions = getPermissionsForOneProject(projectId);
        return selectedProjectPermissions?.permissions || defaultResourceAccess;
    };

    const hasCustomerLevelPermission = (params: HasCustomerLevelPermissionParams) => {
        const { customerId, resource, to } = params;
        const selectedCustomerResourceAccess = getResourceAccessForOneCustomer(customerId);
        const wildcardCustomerResourceAccess = getResourceAccessForOneCustomer('*');
        const access = typeof to === 'object' ? to : [to];

        return [
            access.some(access => selectedCustomerResourceAccess[access]?.includes(resource)),
            access.some(access => wildcardCustomerResourceAccess[access]?.includes(resource)),
            access.some(access => selectedCustomerResourceAccess[access]?.includes('*')),
            access.some(access => wildcardCustomerResourceAccess[access]?.includes('*')),
        ].some(Boolean);
    };

    const hasProjectLevelPermission = (params: HasProjectLevelPermissionParams) => {
        const { projectId, resource, to } = params;
        const selectedProjectResourceAccess = getResourceAccessForOneProject(projectId);
        const wildcardProjectResourceAccess = getResourceAccessForOneProject('*');
        const access = typeof to === 'object' ? to : [to];
        return [
            access.some(access => selectedProjectResourceAccess[access]?.includes(resource)),
            access.some(access => wildcardProjectResourceAccess[access]?.includes(resource)),
            access.some(access => selectedProjectResourceAccess[access]?.includes('*')),
            access.some(access => wildcardProjectResourceAccess[access]?.includes('*')),
        ].some(Boolean);
    };

    const hasProjectLevelPermissionToAnyProject = (params: Omit<HasProjectLevelPermissionParams, 'projectId'>) => {
        const { resource, to } = params;
        const allProjectResourceAccess = getPermissionsForAllProjects();
        const access = typeof to === 'object' ? to : [to];
        return access.some(access =>
            allProjectResourceAccess.some(
                project => project.permissions[access]?.includes(resource) || project.permissions[access]?.includes('*')
            )
        );
    };

    const hasCustomerLevelPermissionToAnyCustomer = (params: Omit<HasCustomerLevelPermissionParams, 'customerId'>) => {
        const { resource, to } = params;
        const allCustomerResourceAccess = getPermissionsForAllCustomers();
        const access = typeof to === 'object' ? to : [to];
        return access.some(access =>
            allCustomerResourceAccess.some(
                customer => customer.permissions[access]?.includes(resource) || customer.permissions[access]?.includes('*')
            )
        );
    };

    const currentUserBelongsToGroup = (group: UserGroup) => {
        return currentUser?.groups?.includes(group) || false;
    };

    const hasPermission = (params: HasPermissionParams) => {
        const { projectId = currentProjectId, customerId = currentCustomerId, resource, to } = params;

        if (
            hasProjectLevelPermission({
                projectId: projectId || undefined,
                resource,
                to,
            })
        ) {
            return true;
        }
        return hasCustomerLevelPermission({
            customerId: customerId || undefined,
            resource,
            to,
        });
    };

    const hasCustomerLevelRole = (params: HasCustomerLevelRoleParams) => {
        if (isCurrentUserSuperAdmin) {
            return true;
        }
        const { customerId = currentCustomerId, role } = params;
        const selectedCustomerRoles = [...getRolesForOneCustomer(customerId), ...getRolesForOneCustomer('*')];
        const roles = typeof role === 'object' ? role : [role];
        return selectedCustomerRoles.length ? roles.every(currentRole => selectedCustomerRoles.includes(currentRole)) : false;
    };

    const hasProjectLevelRole = (params: HasProjectLevelRoleParams) => {
        if (isCurrentUserSuperAdmin) {
            return true;
        }
        const { projectId, role } = params;
        const selectedProjectRoles = [...getRolesForOneProject(projectId), ...getRolesForOneProject('*')];
        const roles = typeof role === 'object' ? role : [role];
        return roles.length ? roles.every(currentRole => selectedProjectRoles.includes(currentRole)) : false;
    };

    return {
        allPermissions: getUserPermissions(),
        getUserPermissions,
        hasCustomerLevelPermission,
        hasProjectLevelPermission,
        hasProjectLevelPermissionToAnyProject,
        hasCustomerLevelPermissionToAnyCustomer,
        hasPermission,
        hasCustomerLevelRole,
        hasProjectLevelRole,
        currentUserBelongsToGroup,
        isCurrentUserInternal: getUserPermissions().is_internal_user,
    };
};

export default usePermissions;
